// Platform-level "Apply All Pending Changes" panel.
//
// Renders at the top of the admin Games hub. Aggregates pending
// changes across every game in the registry into a single banner +
// button. Polling at 10 s keeps the count fresh while an admin
// edits in another tab.
//
// Future-games compat: this component never names a specific game.
// It calls GET /api/admin/config/pending-all which iterates the
// backend registry, and renders whatever `perGame` keys come back.
// New games automatically appear once registered.
//
// The per-game Apply button on each game's own settings page does
// the same thing scoped to that game. This panel is the global
// "push everything that's queued" shortcut.

import { useCallback, useEffect, useState } from 'react';
import Icon from '../../components/Icon';
import { adminApi, getAdminAccessToken } from '../lib/adminApi';

type PendingByGame = {
  lastAppliedAt: string | null;
  pending: Array<{ key: string; updated_at: string; value: unknown }>;
  count: number;
};
type PendingAllPayload = {
  perGame: Record<string, PendingByGame>;
  total: number;
};

type ApplyResult = {
  scope: string;
  log_id: number;
  diff: Record<string, Record<string, unknown>>;
  admin_reload_ok: boolean;
  admin_reload_error: string | null;
  player_reload_ok: boolean;
  player_reload_error: string | null;
  ok: boolean;
};

// 2026-05-20 — 429-remediation final pass. Was 10_000 (6/min) which
// was overkill for a config-pending banner. 30_000 (2/min) is plenty
// — admin config changes are slow-moving and the user is staring at
// the banner while editing, not racing it.
const POLL_MS = 30_000;

function authHeader(): Record<string, string> {
  const t = getAdminAccessToken();
  return t ? { authorization: `Bearer ${t}` } : {};
}

export default function ApplyAllPanel() {
  const [data, setData] = useState<PendingAllPayload | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const j = await adminApi.get<PendingAllPayload>('/admin/config/pending-all');
      setData(j);
    } catch {
      // Non-fatal — banner just stays empty.
      setData(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    // 2026-05-20 — Skip polls when the tab is backgrounded. Matches
    // AgentBottomNav + BulkPayoutsPanel + AgentDashboard. A second
    // admin with this page open in a stale tab no longer contributes
    // to the rate-limit budget while idle.
    const id = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void refresh();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  async function handleApplyAll() {
    setApplying(true);
    setApplyError(null);
    try {
      const res = await fetch('/api/admin/config/apply-all', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...authHeader() },
      });
      const j: ApplyResult = await res.json().catch(() => ({} as ApplyResult));
      if (!res.ok || !j.ok) {
        const reasons: string[] = [];
        if (j.admin_reload_ok === false) {
          reasons.push(`bg-admin reload failed: ${j.admin_reload_error || 'unknown'}`);
        }
        if (j.player_reload_ok === false) {
          reasons.push(`bg-player reload failed: ${j.player_reload_error || 'unknown'}`);
        }
        setApplyError(reasons.length ? reasons.join(' · ') : `Apply failed (HTTP ${res.status})`);
      } else {
        const total = data?.total || 0;
        setFlash(`Applied ${total} change${total === 1 ? '' : 's'} across all games`);
        setTimeout(() => setFlash(null), 3500);
      }
      await refresh();
    } finally {
      setApplying(false);
    }
  }

  // No pending changes anywhere → don't render anything. The hub
  // page stays clean when nothing's queued.
  if (!data || data.total === 0) {
    return flash ? (
      <div className="mb-4 rounded-2xl border border-emerald-400/50 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2 text-[12.5px] font-semibold text-emerald-800 dark:text-emerald-200">
        <Icon name="check_circle" size={16} filled className="inline-block mr-2" />
        {flash}
      </div>
    ) : null;
  }

  const gameIds = Object.entries(data.perGame)
    .filter(([, v]) => v.count > 0)
    .map(([id]) => id);

  return (
    <div className="mb-4 rounded-2xl border-2 border-amber-400/60 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl grid place-items-center bg-amber-200 dark:bg-amber-500/25 shrink-0">
        <Icon name="rocket_launch" size={22} className="text-amber-700 dark:text-amber-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-amber-900 dark:text-amber-200 text-[14px]">
          {data.total} unpublished change{data.total === 1 ? '' : 's'} across {gameIds.length} game{gameIds.length === 1 ? '' : 's'}
        </div>
        <div className="text-[12px] text-amber-800/80 dark:text-amber-300/80 mt-0.5">
          Saved to database — not yet live on the player end. Apply now to push everything in one push.
        </div>
        <div className="text-[11px] text-amber-700/80 dark:text-amber-300/70 mt-1 flex flex-wrap gap-2">
          {gameIds.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-md bg-amber-100 dark:bg-amber-500/15 px-2 py-0.5">
              <span className="font-bold uppercase">{id}</span>
              <span className="opacity-80">×{data.perGame[id].count}</span>
            </span>
          ))}
        </div>
        {applyError && (
          <div className="mt-2 rounded-lg border border-red-400/50 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-[12px] text-red-700 dark:text-red-300 break-words">
            <span className="font-bold">Apply failed: </span>{applyError}
          </div>
        )}
      </div>
      <button
        onClick={handleApplyAll}
        disabled={applying}
        className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
        title="Push every game's saved changes to the live player end now."
      >
        <Icon name={applying ? 'progress_activity' : 'rocket_launch'} size={18}
              className={applying ? 'animate-spin' : ''} />
        {applying ? 'Applying…' : 'Apply All to Live'}
      </button>
    </div>
  );
}
